import { SearchIcon } from '@chakra-ui/icons';
import {
  Button,
  ButtonGroup,
  Divider,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { Column, ColumnFilter, Table } from '@tanstack/react-table';
import { ReactNode, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CiFilter } from 'react-icons/ci';

import NewFilterComponent, { FilterType } from './NewFilterComponent';

export const findFilterById = <TData,>(
  filterExtraInfo: FilterExtraInfo<TData>,
  filterId: string
): FilterData<TData> | undefined => {
  return filterExtraInfo.find((filter) => filter.filterId === filterId);
};

export type FilterData<TData> = {
  filterId: Extract<keyof TData, string>;
  filterType: FilterType;
  queryParamKey?: string;
};

export type FilterExtraInfo<TData> = FilterData<TData>[];

type Props<TData> = {
  columnFilters: ColumnFilter[];
  hiddenFilters?: (keyof TData)[];
  setColumnFilters: (columnFilters: ColumnFilter[]) => void;
  table: Table<TData>;
  filterExtraInfo: FilterExtraInfo<TData>;
};

function Filters<TData>({
  columnFilters,
  hiddenFilters,
  setColumnFilters,
  table,
  filterExtraInfo,
}: Props<TData>) {
  const { t } = useTranslation();
  const [currentSearch, setCurrentSearch] = useState<string>('');
  const [selectedColumn, setSelectedColumn] = useState<Column<TData> | null>(null);

  const onColumnFiltersChange = useCallback(
    (newFilters: ColumnFilter[]) => {
      setColumnFilters(newFilters);
    },
    [setColumnFilters]
  );

  const closeModal = () => {
    setSelectedColumn(null);
  };

  const removeFilter = (id: string) => {
    onColumnFiltersChange(columnFilters.filter((elem) => elem.id !== id));
  };

  return (
    <>
      <Menu isLazy>
        <MenuButton as={IconButton} aria-label="filters" icon={<CiFilter />} bgColor="gray.200" />
        <MenuList p={2}>
          <InputGroup>
            <InputLeftElement pointerEvents="none">
              <Icon as={SearchIcon} color="gray" />
            </InputLeftElement>
            <Input
              value={currentSearch}
              onChange={(e) => setCurrentSearch(e.target.value)}
              placeholder="Search for column name"
            />
          </InputGroup>

          <Divider my={2} />
          {table
            .getAllColumns()
            .filter((c) => !currentSearch || c.id.includes(currentSearch))
            .filter((c) => c.getCanFilter())
            .map((c) => {
              const alreadyHasFilter = columnFilters.find((elem) => elem.id === c.id);
              if (hiddenFilters?.some((key) => c.id === key)) {
                return null;
              }
              return (
                <Tooltip
                  key={c.id}
                  label={alreadyHasFilter ? t('tables.filters.alreadySelected.title') : ''}
                >
                  <MenuItem
                    gap={2}
                    onClick={() => setSelectedColumn(c)}
                    isDisabled={!!alreadyHasFilter}
                  >
                    <Text>{c.columnDef.header as ReactNode}</Text>
                  </MenuItem>
                </Tooltip>
              );
            })}
        </MenuList>
      </Menu>
      <Modal
        isOpen={!!selectedColumn}
        onClose={() => {
          removeFilter(selectedColumn?.id || '');
          closeModal();
        }}
      >
        <ModalOverlay />
        <ModalContent>
          {selectedColumn && (
            <>
              <ModalHeader>
                <Text>Filter by {selectedColumn.columnDef.header as ReactNode}</Text>
              </ModalHeader>
              <ModalBody>
                <NewFilterComponent
                  column={selectedColumn}
                  filterType={
                    findFilterById(filterExtraInfo, selectedColumn.id)?.filterType ||
                    FilterType.STRING
                  }
                  columnFilters={columnFilters}
                  setColumnFilters={onColumnFiltersChange}
                />
              </ModalBody>
              <ModalFooter>
                <ButtonGroup size="sm">
                  <Button
                    variant="outline"
                    onClick={() => {
                      removeFilter(selectedColumn.id);
                      closeModal();
                    }}
                  >
                    Cancel
                  </Button>
                  <Button variant="blue" onClick={closeModal}>
                    Confirm
                  </Button>
                </ButtonGroup>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
}

export default Filters;
